import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import {
  Lock,
  Server,
  KeyRound,
  EyeOff,
  ShieldCheck,
  FileText,
  Clock,
  AlertTriangle,
} from "lucide-react";

const pillars = [
  {
    icon: ShieldCheck,
    title: "HIPAA-aligned practices",
    desc: "Our workflows, vendor contracts, and clinical documentation follow HIPAA standards for protected health information (PHI). Vendor relationships involving PHI are governed by Business Associate Agreements (BAAs).",
  },
  {
    icon: Lock,
    title: "Encrypted in transit and at rest",
    desc: "Every page is served over HTTPS (TLS 1.2+). Stored records, uploaded documents, and database backups are encrypted at rest using industry-standard AES-256.",
  },
  {
    icon: KeyRound,
    title: "Access controls and audit logging",
    desc: "Administrative access is restricted to authorized staff with role-based permissions. Sensitive admin actions, such as status changes and document downloads, are recorded in a tamper-evident audit log.",
  },
  {
    icon: EyeOff,
    title: "No data selling. No data sharing.",
    desc: "We never sell your information and we never share it with advertisers, brokers, or social platforms. Information you share with us is used only to deliver and improve your care.",
  },
];

const safeguards = [
  { icon: Server, label: "Encrypted databases and backups" },
  { icon: Lock, label: "TLS 1.2 or higher in transit" },
  { icon: KeyRound, label: "bcrypt password hashing" },
  { icon: ShieldCheck, label: "Brute-force lockout on admin login" },
  { icon: Clock, label: "Short-lived, HttpOnly session cookies" },
  { icon: FileText, label: "Audit log on admin actions" },
  { icon: EyeOff, label: "No third-party advertising trackers" },
  { icon: ShieldCheck, label: "HIPAA-aligned hosting partners under BAA" },
];

export default function Privacy() {
  return (
    <SiteLayout>
      <SEO
        title="Privacy, HIPAA & Security"
        description="How Golden Life Standard Health protects your medical information: HIPAA safeguards, encryption in transit and at rest, breach notification policy, and your rights."
        path="/privacy"
      />
      <section data-testid="privacy-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Security and privacy</p>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
              Your information, <span className="italic text-sage-600">protected with the same care as your appointment.</span>
            </h1>
            <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
              We built Golden Life Standard Health to treat trust as a clinical responsibility. Below is a plain-language summary of how we handle your information, the technical safeguards in place, and the choices you have.
            </p>
          </div>
          <aside className="lg:col-span-4">
            <div className="card-soft p-6 flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-heading font-semibold text-ink">A note on language</p>
                <p className="mt-2 text-sm text-muted_text leading-relaxed">
                  We avoid jargon here. If anything below is unclear, please write to <a href="mailto:care@goldenlifesh.com" className="text-sage-700 hover:text-gold-600 font-medium">care@goldenlifesh.com</a> and a real person will reply within one business day.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section data-testid="privacy-pillars" className="container-pad max-w-7xl mx-auto pb-12">
        <div className="grid sm:grid-cols-2 gap-6">
          {pillars.map((p, i) => (
            <div key={p.title} data-testid={`privacy-pillar-${i}`} className="card-soft p-7">
              <div className="w-12 h-12 rounded-2xl bg-gold-500/15 text-gold-600 flex items-center justify-center">
                <p.icon className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h2 className="mt-5 font-heading text-xl font-semibold text-ink">{p.title}</h2>
              <p className="mt-3 text-sm text-muted_text leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="privacy-safeguards" className="bg-paper border-y border-[#E8EBE4]">
        <div className="container-pad max-w-7xl mx-auto py-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Technical safeguards</p>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
                Quiet, careful protection, layered front to back.
              </h2>
              <p className="mt-4 text-muted_text leading-relaxed max-w-md">
                These are the day-to-day controls that keep your records private. We continue to invest in them as our practice grows.
              </p>
            </div>
            <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3" data-testid="safeguard-list">
              {safeguards.map((s, i) => (
                <li key={s.label} data-testid={`safeguard-${i}`} className="card-soft p-5 inline-flex items-start gap-3 text-ink text-sm">
                  <span className="w-9 h-9 rounded-xl bg-sage-50 text-sage-700 inline-flex items-center justify-center"><s.icon className="w-4 h-4" strokeWidth={1.75} /></span>
                  <span className="pt-1.5 leading-snug">{s.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="privacy-your-data" className="container-pad max-w-7xl mx-auto py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> What we collect</p>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
            Only what helps us care for you.
          </h2>
          <p className="mt-4 text-muted_text leading-relaxed max-w-md">
            We collect the minimum information needed to schedule, deliver, and follow up on care. Required fields are clearly marked at point of collection.
          </p>
        </div>
        <div className="lg:col-span-7 space-y-5 text-muted_text leading-relaxed">
          <p><span className="font-semibold text-ink">Identifying information:</span> name, date of birth, contact details, address. Used for appointment scheduling and clinical records.</p>
          <p><span className="font-semibold text-ink">Insurance details:</span> plan, member ID, group number, and optional uploaded card images. Used to verify benefits and process claims.</p>
          <p><span className="font-semibold text-ink">Clinical information:</span> intake responses, symptoms, history, treatment notes. Used by your care team to build and adjust your plan.</p>
          <p><span className="font-semibold text-ink">Visit data:</span> appointment timestamps and clinician notes. Stored under HIPAA-aligned retention policies.</p>
          <p className="text-sm text-muted_text/90">We do not use analytics or marketing pixels that share personal information with third parties. We do use standard, privacy-preserving operational logs to keep the site reliable.</p>
        </div>
      </section>

      <section data-testid="privacy-your-rights" className="bg-cream border-t border-[#E8EBE4]">
        <div className="container-pad max-w-7xl mx-auto py-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Your rights</p>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
              You are in control of your record.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4 text-ink">
            <li className="card-soft p-5"><span className="font-semibold">Access.</span> You may request a copy of the information we hold about you.</li>
            <li className="card-soft p-5"><span className="font-semibold">Correction.</span> You may ask us to correct inaccuracies in your record.</li>
            <li className="card-soft p-5"><span className="font-semibold">Restriction.</span> You may ask us to limit how we use or share certain information, subject to clinical and legal requirements.</li>
            <li className="card-soft p-5"><span className="font-semibold">Complaint.</span> You may contact our Privacy Officer or file a complaint with the U.S. Department of Health and Human Services Office for Civil Rights without retaliation.</li>
          </ul>
        </div>
      </section>

      <section data-testid="privacy-emergency" className="container-pad max-w-7xl mx-auto py-16">
        <div className="card-soft p-7 sm:p-9 flex flex-col sm:flex-row items-start gap-5 border-l-4 border-gold-500">
          <div className="w-12 h-12 rounded-2xl bg-gold-500/15 text-gold-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-ink">In a crisis, please call for help</h3>
            <p className="mt-3 text-muted_text leading-relaxed">
              This page describes our standard data practices. It is not a place to request emergency care. If you or a loved one are in immediate danger or having thoughts of self-harm, call <span className="font-semibold text-ink">988</span> for the Suicide and Crisis Lifeline or <span className="font-semibold text-ink">911</span> for emergency services.
            </p>
          </div>
        </div>
      </section>

      <section data-testid="privacy-contact" className="container-pad max-w-7xl mx-auto pb-24">
        <div className="rounded-3xl bg-ink text-cream/90 p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
          <div className="flex-1">
            <p className="eyebrow text-gold-400/80"><span className="w-6 h-px bg-gold-500" /> Reach our Privacy Officer</p>
            <h3 className="mt-3 font-heading text-2xl sm:text-3xl tracking-tight">
              Questions, requests, or concerns about your data
            </h3>
            <p className="mt-3 text-cream/70 max-w-xl">
              Email <a href="mailto:care@goldenlifesh.com" className="text-gold-300 hover:text-gold-200 font-medium">care@goldenlifesh.com</a> and a member of our privacy and care team will respond within one business day.
            </p>
          </div>
          <Link to="/contact" data-testid="privacy-contact-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 hover:bg-gold-600 text-forest-800 font-semibold px-6 py-3 transition-colors">
            Contact our team
          </Link>
        </div>
        <p className="mt-6 text-xs text-muted_text/80 max-w-3xl">
          Last reviewed May 2026. This page summarises our practices in plain language and is not a substitute for the full Notice of Privacy Practices, which is provided during intake and available on request.
        </p>
      </section>
    </SiteLayout>
  );
}
